import { useEffect, useState } from "react";
import { Link } from "wouter";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import AppLayout from "@/components/app-layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MessageSquare, Phone, Trash2, Loader2, ArrowRight, RefreshCw, Sparkles, Lock } from "lucide-react";
import { format } from "date-fns";
import { hasFeatureAccess, type AccountType } from "@shared/schema";
import { UpgradeModal } from "@/components/upgrade-modal";
import { usePageTitle } from "@/hooks/use-page-title";
import { useBetaAccess } from "@/hooks/use-beta-access";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";

type MessagingState = {
  messagingPhoneNumber: string | null;
  messagingPhoneNumberSid: string | null;
  messagingPhoneAssignedAt: string | null;
};

type CurrentUser = {
  phone: string | null;
};

type AuthMeResponse = {
  user: { id: number; email: string; selectedPlan?: string };
  effectivePlan?: AccountType;
};

// Best-effort prefill: pull a 3-digit US area code out of the host's profile
// phone so the area-code input starts with something plausible without
// forcing them to type it. We accept +1XXXNNNNNNN, 1XXXNNNNNNN, or
// XXXNNNNNNN and fall back to "" otherwise. The host can still clear or
// change it before pressing "Get a number".
function inferAreaCodeFromPhone(phone: string | null | undefined): string {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) return digits.slice(1, 4);
  if (digits.length === 10) return digits.slice(0, 3);
  return "";
}

export default function AppAccountMessaging() {
  const queryClient = useQueryClient();
  usePageTitle("Messaging Settings");
  const { toast } = useToast();
  const meQuery = useQuery<CurrentUser>({ queryKey: ["/api/app/profile"] });
  const profileAreaCode = inferAreaCodeFromPhone(meQuery.data?.phone);
  const [areaCode, setAreaCode] = useState("");
  const [areaCodeTouched, setAreaCodeTouched] = useState(false);
  const [replaceAreaCode, setReplaceAreaCode] = useState("");
  const [confirmRelease, setConfirmRelease] = useState(false);
  const [confirmReplace, setConfirmReplace] = useState(false);

  // Best-effort prefill once the host's profile loads. We only fill if the
  // input is still empty AND the host hasn't typed in it yet, so we never
  // overwrite their input.
  useEffect(() => {
    if (profileAreaCode && !areaCodeTouched && areaCode === "") {
      setAreaCode(profileAreaCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileAreaCode]);

  // Gate the page on Professional. Server enforces the same via requireTier.
  const authMeQuery = useQuery<AuthMeResponse>({
    queryKey: ["/api/app/auth/me"],
  });
  const effectivePlan = (authMeQuery.data?.effectivePlan ||
    authMeQuery.data?.user?.selectedPlan ||
    "Host") as AccountType;
  const planResolved = !!authMeQuery.data;
  const isModularAccount =
    (authMeQuery.data?.user as { accountKind?: string | null } | undefined)?.accountKind != null;
  // Modular accounts have no legacy tier — the messaging_provisioning beta
  // below is the sole gate for them.
  const hasProfessionalAccess = planResolved
    ? isModularAccount || hasFeatureAccess(effectivePlan, "Professional")
    : true;

  // Task #2287 — additionally beta-gated. Beta grant is checked in addition
  // to Professional plan; users with Professional but no beta see their current
  // number in read-only form (if they have one) but cannot provision/replace/release.
  const hasMessagingBeta = useBetaAccess("messaging_provisioning");

  // Always fetch the current state so existing number holders can see their
  // number in read-only even without the beta grant.
  const stateQuery = useQuery<MessagingState>({
    queryKey: ["/api/app/account/messaging"],
    enabled: hasProfessionalAccess,
  });

  const getNumberMutation = useMutation({
    mutationFn: async (code: string) => {
      const res = await apiRequest("POST", "/api/app/account/messaging/get-number", {
        areaCode: code || undefined,
      });
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/account/messaging"] });
      toast({
        title: "Messaging number assigned",
        description: data?.messagingPhoneNumber
          ? `You're now sending direct member SMS from ${data.messagingPhoneNumber}.`
          : undefined,
      });
    },
    onError: (err: any) => {
      toast({
        title: "Couldn't get a number",
        description: parseErrorMessage(err, "Unknown error"),
        variant: "destructive",
      });
    },
  });

  const replaceMutation = useMutation({
    mutationFn: async (code: string) => {
      const res = await apiRequest("POST", "/api/app/account/messaging/replace", {
        areaCode: code || undefined,
      });
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/account/messaging"] });
      setConfirmReplace(false);
      setReplaceAreaCode("");
      toast({
        title: "Messaging number replaced",
        description: data?.messagingPhoneNumber
          ? `Now sending from ${data.messagingPhoneNumber}.`
          : undefined,
      });
    },
    onError: (err: any) => {
      toast({
        title: "Couldn't replace number",
        description: parseErrorMessage(err, "Unknown error"),
        variant: "destructive",
      });
    },
  });

  const releaseMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", "/api/app/account/messaging");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/account/messaging"] });
      setConfirmRelease(false);
      toast({ title: "Messaging number released" });
    },
    onError: (err: any) => {
      toast({
        title: "Failed to release number",
        description: parseErrorMessage(err, "Unknown error"),
        variant: "destructive",
      });
    },
  });

  const current = stateQuery.data;
  const hasNumber = !!current?.messagingPhoneNumber;

  const [upgradeOpen, setUpgradeOpen] = useState(true);
  if (planResolved && !hasProfessionalAccess) {
    return (
      <AppLayout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-1">
              <MessageSquare className="h-5 w-5 text-brand" />
              <h1
                className="text-xl font-semibold text-gray-900"
                data-testid="text-messaging-heading"
              >
                Messaging Number
              </h1>
            </div>
            <p className="text-sm text-gray-500">
              Get your own dedicated phone number for texting your direct-booked
              members.
            </p>
          </div>
          <Card
            className="p-8 text-center border-amber-200 bg-amber-50"
            data-testid="card-messaging-upgrade-required"
          >
            <div className="mx-auto w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mb-3">
              <Lock className="h-6 w-6 text-amber-600" />
            </div>
            <h2 className="text-lg font-semibold text-amber-900 mb-1">
              Professional plan required
            </h2>
            <p className="text-sm text-amber-800 mb-4">
              Your own dedicated messaging number is part of the Professional
              plan. Your current plan is{" "}
              <span className="font-medium" data-testid="text-current-plan">
                {effectivePlan}
              </span>
              .
            </p>
            <div className="flex justify-center gap-2">
              <Link href="/app/account/billing">
                <Button data-testid="button-upgrade-to-professional">
                  Upgrade to Professional
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
              <Button
                variant="outline"
                onClick={() => setUpgradeOpen(true)}
                data-testid="button-show-upgrade-details"
              >
                See plan details
              </Button>
            </div>
          </Card>
          <UpgradeModal
            open={upgradeOpen}
            onOpenChange={setUpgradeOpen}
            feature="Dedicated Messaging Number"
            requiredTier="Professional"
            reason="A dedicated messaging number is a Professional-plan feature so we can keep deliverability tight and per-host attribution clean."
          />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <MessageSquare className="h-5 w-5 text-brand" />
              <h1
                className="text-xl font-semibold text-gray-900"
                data-testid="text-messaging-heading"
                data-tour="account-messaging-heading"
              >
                Messaging Number
              </h1>
            </div>
            <p className="text-sm text-gray-500">
              {hasMessagingBeta
                ? "Get your own dedicated phone number for texting your direct-booked members. Replies route back into your conversations. PadSplit members are messaged through PadSplit Messenger automatically."
                : "Your dedicated phone number for texting your direct-booked members."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ExplainerVideoButton pageKey="account-messaging" />
            <GuideButton pageKey="account-messaging" />
          </div>
        </div>

        <GuideNudgeBanner pageKey="account-messaging" />

        <Card className="p-6 mb-6" data-tour="account-messaging-current">
          <h2 className="text-base font-semibold text-gray-900 mb-4">
            Current number
          </h2>
          {stateQuery.isLoading ? (
            <Skeleton className="h-10 w-64" />
          ) : hasNumber ? (
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-brand" />
                <div>
                  <div className="flex items-center gap-2">
                    <div
                      className="text-lg font-semibold text-gray-900"
                      data-testid="text-assigned-number"
                    >
                      {current!.messagingPhoneNumber}
                    </div>
                    {current!.messagingPhoneNumberSid?.startsWith("PNE2E") && (
                      <Badge
                        variant="outline"
                        className="text-[10px] px-1.5 py-0"
                        data-testid="badge-assigned-number-test"
                      >
                        Test number
                      </Badge>
                    )}
                  </div>
                  {current!.messagingPhoneAssignedAt && (
                    <div className="text-xs text-gray-500">
                      Assigned{" "}
                      {format(new Date(current!.messagingPhoneAssignedAt), "PP")}
                    </div>
                  )}
                </div>
                <Badge variant="secondary">Active</Badge>
              </div>
              {hasMessagingBeta && (
                <div className="sm:ml-auto flex flex-col sm:flex-row gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setConfirmReplace(true)}
                    disabled={replaceMutation.isPending}
                    data-testid="button-replace-number"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Replace with different area code
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => setConfirmRelease(true)}
                    disabled={releaseMutation.isPending}
                    data-testid="button-release-number"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Release number
                  </Button>
                </div>
              )}
            </div>
          ) : hasMessagingBeta ? (
            <div className="text-sm text-gray-600" data-testid="text-no-messaging-number">
              You don't have a dedicated messaging number yet. Click "Get a number" below
              and we'll assign one for you in seconds.
            </div>
          ) : null}
        </Card>

        {hasMessagingBeta && !hasNumber && (
          <Card className="p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-2">
              Get a number
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              We'll pick the first available SMS-enabled US number. Specify an
              area code if you want one local to your members.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <Label htmlFor="area-code" className="text-xs text-gray-600">
                  Area code (optional)
                </Label>
                <Input
                  id="area-code"
                  inputMode="numeric"
                  maxLength={3}
                  placeholder="e.g. 415"
                  value={areaCode}
                  onChange={(e) => {
                    setAreaCodeTouched(true);
                    setAreaCode(e.target.value.replace(/\D/g, "").slice(0, 3));
                  }}
                  data-testid="input-area-code"
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={() => getNumberMutation.mutate(areaCode)}
                  disabled={getNumberMutation.isPending}
                  data-testid="button-get-number"
                >
                  {getNumberMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4 mr-2" />
                  )}
                  Get a number
                </Button>
              </div>
            </div>
          </Card>
        )}

        <div className="mt-6 text-sm text-gray-600" data-tour="account-messaging-billing-link">
          <Link
            href="/app/account/billing"
            className="text-brand underline inline-flex items-center gap-1"
            data-testid="link-back-to-billing"
          >
            Back to Billing <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>

      <Dialog open={confirmReplace} onOpenChange={(open) => { if (!open && replaceMutation.isPending) return; setConfirmReplace(open); }}>
        <DialogContent
          onEscapeKeyDown={(e) => { if (replaceMutation.isPending) e.preventDefault(); }}
          onInteractOutside={(e) => { if (replaceMutation.isPending) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (replaceMutation.isPending) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle>Replace with a different area code?</DialogTitle>
            <DialogDescription>
              Your current number {current?.messagingPhoneNumber} will be released
              and replaced. Inbound replies sent to the old number after this won't
              reach you.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="replace-area-code" className="text-xs text-gray-600">
              New area code (optional)
            </Label>
            <Input
              id="replace-area-code"
              inputMode="numeric"
              maxLength={3}
              placeholder="e.g. 415"
              value={replaceAreaCode}
              onChange={(e) =>
                setReplaceAreaCode(e.target.value.replace(/\D/g, "").slice(0, 3))
              }
              data-testid="input-replace-area-code"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmReplace(false)}
              data-testid="button-cancel-replace"
            >
              Cancel
            </Button>
            <Button
              onClick={() => replaceMutation.mutate(replaceAreaCode)}
              disabled={replaceMutation.isPending}
              data-testid="button-confirm-replace"
            >
              {replaceMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Replace
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmRelease} onOpenChange={(open) => { if (!open && releaseMutation.isPending) return; setConfirmRelease(open); }}>
        <DialogContent
          onEscapeKeyDown={(e) => { if (releaseMutation.isPending) e.preventDefault(); }}
          onInteractOutside={(e) => { if (releaseMutation.isPending) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (releaseMutation.isPending) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle>Release messaging number?</DialogTitle>
            <DialogDescription>
              Direct member SMS will stop working until you assign a new number.
              PadSplit conversations are unaffected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmRelease(false)}
              data-testid="button-cancel-release"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => releaseMutation.mutate()}
              disabled={releaseMutation.isPending}
              data-testid="button-confirm-release"
            >
              {releaseMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Release
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
